<template>
  <v-card class="pa-8 border-card text-center shadow-summary h-100 d-flex flex-column" rounded="xl" style="background: white;">
    <div class="mb-6 text-left">
      <div class="text-overline font-weight-black text-slate-500 tracking-wider mb-1">
        CONFIGURATION SUMMARY
      </div>
      <h3 class="text-h5 font-weight-black text-slate-800 mb-0">
        {{ form.acquisition_type === 'lease' ? 'Lease Overview' : 'Purchase Overview' }}
      </h3>
    </div>

    <!-- Specs Table -->
    <div class="specs-table mb-6 text-left flex-grow-1">
      <template v-if="cart_items.length <= 1">
        <div class="d-flex justify-space-between py-3 border-bottom-thin">
          <span class="font-weight-bold text-slate-500">Machine</span>
          <span class="font-weight-black text-slate-800">{{ copier?.instance?.name }}</span>
        </div>
      </template>
      <template v-else>
        <div class="py-2 border-bottom-thin">
          <span class="font-weight-bold text-slate-500 d-block mb-2">Configured Machines ({{ cart_items.length }})</span>
          <div v-for="(item, idx) in cart_items" :key="idx" class="d-flex justify-space-between align-center py-1.5 pl-3 border-left text-body-2 mb-1" style="border-left: 2px solid #e06927 !important;">
            <span class="text-slate-700 font-weight-medium truncate mr-4" style="max-width: 65%;">{{ item.product.name }}</span>
            <div class="d-flex align-center gap-2 flex-shrink-0">
              <span class="font-weight-black text-slate-800" v-if="ConfigShowPricing && form.acquisition_type === 'lease' && !isItemUnpriced(item)">
                ${{ CalculateProductMonthlyPrice(item.product, item.datasets, item.accessories).toLocaleString() }}/mo
              </span>
              <span class="text-caption font-weight-bold text-slate-400" v-else>
                {{ item.product.printer_category || 'Copier' }}
              </span>
              <v-btn
                v-if="cart_items.length > 1"
                icon="mdi-trash-can-outline"
                variant="text"
                density="comfortable"
                color="red-darken-2"
                size="small"
                @click="$emit('remove-item', idx)"
              />
            </div>
          </div>
        </div>
      </template>

      <div class="d-flex justify-space-between py-3 border-bottom-thin">
        <span class="font-weight-bold text-slate-500">Term</span>
        <span class="font-weight-black text-slate-800">
          {{ form.acquisition_type === 'lease' ? form.lease_term + ' month lease' : 'Full purchase' }}
        </span>
      </div>
      <template v-if="form.acquisition_type === 'lease' && cart_items.length <= 1">
        <div class="d-flex justify-space-between py-3 border-bottom-thin">
          <span class="font-weight-bold text-slate-500">Black and white</span>
          <span class="font-weight-black text-slate-800">{{ copier?.instance?.prints_per_month?.toLocaleString() }} pages</span>
        </div>
        <div class="d-flex justify-space-between py-3 border-bottom-thin" v-if="copier?.instance?.copier_color !== 'B&W'">
          <span class="font-weight-bold text-slate-500">Color</span>
          <span class="font-weight-black text-slate-800">{{ copier?.instance?.color_volume?.toLocaleString() }} pages</span>
        </div>
      </template>
      <template v-if="form.acquisition_type === 'lease'">
        <div class="d-flex justify-space-between py-3 border-bottom-thin">
          <span class="font-weight-bold text-slate-500">Service and toner</span>
          <span class="font-weight-bold text-green-darken-3">Included</span>
        </div>
      </template>
      <div v-if="free_shipping" class="d-flex justify-space-between py-3 border-bottom-thin">
        <span class="font-weight-bold text-slate-500">Delivery</span>
        <span class="font-weight-bold text-green-darken-3">Free</span>
      </div>
      <div v-if="ConfigShowPricing && is_lease_priced" class="d-flex justify-space-between align-center py-3 border-bottom-thin">
        <span class="font-weight-bold text-slate-500">Estimated Monthly</span>
        <span class="font-weight-black text-slate-800 d-inline-flex align-center">
          $<Odometer :value="cartTotalMonthlyPrice || 0" />/mo
        </span>
      </div>
      <div v-if="ConfigShowPricing && is_lease_priced" class="py-2 text-caption text-grey-darken-1" style="font-size: 10px; line-height: 1.35;">
        Displayed prices and monthly lease payments are estimates for informational purposes. Final pricing, availability, lease terms, taxes, delivery, installation, and service arrangements are determined by the participating dealer and applicable financing provider. Financing is subject to approval.
      </div>
      <div v-else-if="ConfigShowPricing" class="d-flex justify-space-between align-center py-3 border-bottom-thin">
        <span class="font-weight-bold text-slate-500">Estimated Monthly</span>
        <span class="font-weight-bold text-slate-600">Contact for pricing</span>
      </div>
    </div>

    <!-- Green Notice Banner -->
    <div class="notice-banner py-3 px-4 text-body-2 font-weight-bold text-green-darken-4 rounded-lg mb-4 text-center">
      This is your equipment configuration summary. No obligation to continue.
    </div>

    <!-- Trust Badges -->
    <div class="text-caption font-weight-bold text-slate-500">
      ★ 5.0 Google, 1,300+ reviews &bull; BBB A+ Accredited
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import Odometer from './Odometer.vue'
import { CalculateProductMonthlyPrice, IsProductCustomQuote } from '@/mixins/PricingMixins.ts'

const props = defineProps<{
  copier?: {
    instance: any
    datasets: any[]
    accessories: any[]
  }
  cartTotalMonthlyPrice?: number
}>()

defineEmits<{
  (e: 'remove-item', idx: number): void
}>()

const store = useGlobalsStore()
const { form, cart_items, free_shipping, ConfigShowPricing } = storeToRefs(store)

const isCartUnpriced = computed(() => {
  return cart_items.value.every((item: any) => IsProductCustomQuote(item.product))
})

const isItemUnpriced = (item: any) => {
  return IsProductCustomQuote(item?.product)
}

const is_lease_priced = computed(() => {
  return form.value.acquisition_type === 'lease' && !isCartUnpriced.value && Number(props.cartTotalMonthlyPrice || 0) > 0
})
</script>

<style scoped lang="scss">
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.shadow-summary {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
}
.border-bottom-thin {
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}
.notice-banner {
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
}
.text-slate-800 {
  color: #1e293b;
}
.text-slate-500 {
  color: #64748b;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
